import useLocalState from '@phntms/use-local-state';
import { camelCase, debounce, kebabCase, upperFirst } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

import { ReminderList } from '../../compositions/reminders/list/reminder-list';
import { ReminderListHeader } from '../../compositions/reminders/list/reminder-list-header';
import { ReminderModal } from '../../compositions/reminders/modals/reminder-modal/reminder-modal';
import { useAppContext } from '../../data/context/app-context';
import { ReminderSortField } from '../../data/data/reminders/enums';
import { useReminderGroupItems } from '../../data/data/reminders/use-reminder-group-items';
import { useReminderItems } from '../../data/data/reminders/use-reminder-items';
import {
  BaseReminderItemDataFragment,
  BaseReminderSortInput,
  ReminderFrequencyGroup,
  ReminderGroupItemDataFragment,
  SortEnumType,
} from '../../data/types/api/graphql';
import { useNavigateWithHealthProvider } from '../../data/utils/router.utils';
import { Button, ButtonIcon } from '../../elements/button/button';
import { Content } from '../../elements/content/content';
import { ContentMessage } from '../../elements/content/content-message';
import { BaseInputIcon } from '../../elements/form/input/base-input';
import { InputField } from '../../elements/form/input/input-field';
import { CustomFormattedMessage } from '../../elements/intl/custom-formatted-message';
import { Sidebar } from '../../elements/sidebar/sidebar';
import { SidebarList } from '../../elements/sidebar-list/sidebar-list';
import { SidebarListItem } from '../../elements/sidebar-list/sidebar-list-item';
import {
  DataTableLocationState,
  DataTablePageSize,
} from '../../elements/table/data-table/data-table';
import {
  Pagination,
  PaginationData,
} from '../../elements/table/pagination/pagination';

export function RemindersPage() {
  const intl = useIntl();

  const { healthProviderId } = useAppContext();

  const { group } = useParams();

  const [reminderCreateModalOpen, setReminderCreateModalOpen] = useState<
    string | null | false
  >(false);

  const { data: reminderGroupItemsData, loading: reminderGroupItemsLoading } =
    useReminderGroupItems(healthProviderId);

  const location = useLocation();

  const selectedGroup = useMemo(() => {
    if (group !== undefined) return group;
    if (reminderGroupItemsData.length > 0)
      return kebabCase(reminderGroupItemsData[0].key);
    return undefined;
  }, [group, reminderGroupItemsData]);

  const navigate = useNavigateWithHealthProvider();

  const [limit, setLimit] = useLocalState<number>(
    'dtRemindersLimit',
    DataTablePageSize
  );

  const [paging, setPaging] = useState<PaginationData>({
    page: (location?.state as DataTableLocationState)?.page ?? 1,
    total: 0,
  });

  const [query, setQuery] = useState<string>(
    (location?.state as DataTableLocationState)?.query ?? ''
  );

  const [sortField, setSortField] = useState<ReminderSortField>(
    ReminderSortField.NextSendDate
  );

  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const { data: reminderItemsData, loading: reminderItemsLoading } =
    useReminderItems({
      healthProviderId,
      group: upperFirst(
        camelCase(selectedGroup ?? '')
      ) as ReminderFrequencyGroup,
      take: limit,
      skip: (paging.page - 1) * limit,
      search: query,
      sort: [
        {
          [sortField]:
            sortOrder === 'asc' ? SortEnumType.Asc : SortEnumType.Desc,
        } as BaseReminderSortInput,
      ],
    });

  const reminderItems = useMemo(() => {
    const total = reminderItemsData?.totalCount ?? 0;

    if (reminderItemsData) {
      setPaging(prevState => ({
        ...prevState,
        total,
      }));
    }

    return reminderItemsData?.items ?? [];
  }, [reminderItemsData]);

  useEffect(() => {
    if (query.length > 0) {
      setPaging(prevState => ({
        ...prevState,
        page: 1,
      }));
    }
  }, [query]);

  const onHandleReminderGroup = useCallback(
    (value: ReminderGroupItemDataFragment) => {
      navigate(`/reminders/${kebabCase(value.key)}`);
    },
    [navigate]
  );

  const onHandleSearch = useCallback((value: string) => {
    setQuery(value);
  }, []);

  const onHandleSearchDebounce = useMemo(
    () => debounce(onHandleSearch, 250),
    [onHandleSearch]
  );

  const onHandleSortChange = useCallback(
    (field: ReminderSortField, order: 'asc' | 'desc') => {
      setSortField(field);
      setSortOrder(order);
      setPaging(prevState => ({
        ...prevState,
        page: 1,
      }));
    },
    []
  );

  const onHandleLimit = useCallback(
    (value: number) => {
      setLimit(value);

      setPaging(prevState => ({
        ...prevState,
        page: 1,
      }));
    },
    [setLimit]
  );

  const onHandlePagination = useCallback((value: number) => {
    setPaging(prevState => ({
      ...prevState,
      page: value,
    }));
  }, []);

  useEffect(() => {
    setPaging(prevState => ({
      ...prevState,
      page: 1,
    }));
  }, [healthProviderId]);

  const onHandleReminder = useCallback(
    (value: BaseReminderItemDataFragment) => {
      setReminderCreateModalOpen(value?.id);
    },
    []
  );

  return (
    <>
      <Sidebar
        title={<CustomFormattedMessage id="reminders.title" />}
        toolbar={
          <Button
            icon={ButtonIcon.REMINDER_ADD}
            onClick={() => setReminderCreateModalOpen(null)}
          >
            <CustomFormattedMessage id="reminders.button.create-reminder" />
          </Button>
        }
      >
        <SidebarList
          loading={reminderGroupItemsLoading}
          items={reminderGroupItemsData.map(option => (
            <SidebarListItem
              key={option.key}
              title={
                <CustomFormattedMessage
                  id={`reminders.group.${kebabCase(option.key)}`}
                />
              }
              values={{
                value: option.value,
              }}
              active={selectedGroup === kebabCase(option.key)}
              onClick={() => onHandleReminderGroup(option)}
            />
          ))}
        />
      </Sidebar>

      <Content
        toolbar={
          selectedGroup ? (
            <InputField
              icon={BaseInputIcon.SEARCH}
              type="search"
              name="search"
              placeholder={intl.formatMessage({
                id: 'reminders.search-placeholders',
              })}
              onChange={value => onHandleSearchDebounce(value)}
            />
          ) : undefined
        }
      >
        {!selectedGroup && (
          <ContentMessage
            message={<CustomFormattedMessage id="reminders.select-group" />}
          />
        )}

        {selectedGroup && (
          <>
            <ReminderListHeader
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={onHandleSortChange}
            />

            <ReminderList
              loading={reminderItemsLoading}
              items={reminderItems}
              onClick={onHandleReminder}
            />

            <Pagination
              limit={limit}
              paging={paging}
              onChangeLimit={onHandleLimit}
              onChangePage={onHandlePagination}
            />
          </>
        )}
      </Content>

      {reminderCreateModalOpen !== false && (
        <ReminderModal
          reminderId={reminderCreateModalOpen ?? null}
          healthProviderId={healthProviderId}
          onClose={() => {
            setReminderCreateModalOpen(false);
          }}
        />
      )}
    </>
  );
}
